/**
 * Stack Builder - Interactive "Build Your Own Stack" section
 * Manages selection state, sticky bottom bar, multi-item ATC with bundle grouping,
 * discount tier messaging, and quick-view modal.
 */
(function () {
  'use strict';

  function StackBuilder(config) {
    this.sectionId = config.sectionId;
    this.tier1Count = config.tier1Count || 2;
    this.tier1Label = config.tier1Label || '5% off';
    this.tier2Count = config.tier2Count || 3;
    this.tier2Label = config.tier2Label || '10% off';
    // Numeric % parsed from the tier labels (e.g. "15% off" -> 15) for live savings math.
    // Source of truth is the same label the page advertises, so on-page $ matches the EPD discount.
    this.tier1Pct = parseInt(String(this.tier1Label).replace(/[^0-9]/g, ''), 10) || 0;
    this.tier2Pct = parseInt(String(this.tier2Label).replace(/[^0-9]/g, ''), 10) || 0;
    this.ctaLabel = config.ctaLabel || 'Add Stack to Cart';
    this.showDiscountMessaging = config.showDiscountMessaging !== false;
    // Free-shipping nudge. Threshold in cents; measured against the DISCOUNTED total so
    // it can never contradict the cart's free-shipping bar (which uses cart.total_price @ $80).
    this.freeShipThreshold = config.freeShipThreshold || 0;
    this.showFreeShipping = config.showFreeShipping !== false && this.freeShipThreshold > 0;
    this.selectedItems = [];

    this.section = document.querySelector('.stack-builder-' + this.sectionId);
    if (!this.section) return;

    this.stickyBar = document.getElementById('sb-sticky-bar-' + this.sectionId);
    this.barItems = document.getElementById('sb-bar-items-' + this.sectionId);
    this.barCount = document.getElementById('sb-bar-count-' + this.sectionId);
    this.barMessage = document.getElementById('sb-bar-message-' + this.sectionId);
    this.barShip = document.getElementById('sb-bar-ship-' + this.sectionId);
    this.barCta = document.getElementById('sb-bar-cta-' + this.sectionId);
    this.barProgressFill = document.getElementById('sb-bar-progress-fill-' + this.sectionId);
    this.barProgressStop1 = document.getElementById('sb-bar-progress-stop-1-' + this.sectionId);
    this.barProgressStop2 = document.getElementById('sb-bar-progress-stop-2-' + this.sectionId);
    this.toast = document.getElementById('sb-toast-' + this.sectionId);
    this.toastMeta = document.getElementById('sb-toast-meta-' + this.sectionId);
    this._toastTimer = null;

    // Sticky top tier ribbon
    this.topTierBar = document.getElementById('sb-top-tier-bar-' + this.sectionId);
    this.topTierMsg = document.getElementById('sb-top-tier-msg-' + this.sectionId);

    // Modal elements
    this.modalOverlay = document.getElementById('sb-modal-overlay-' + this.sectionId);
    this.modalImg = document.getElementById('sb-modal-img-' + this.sectionId);
    this.modalTitle = document.getElementById('sb-modal-title-' + this.sectionId);
    this.modalPrice = document.getElementById('sb-modal-price-' + this.sectionId);
    this.modalDesc = document.getElementById('sb-modal-desc-' + this.sectionId);
    this.modalVariants = document.getElementById('sb-modal-variants-' + this.sectionId);
    this.modalAddBtn = document.getElementById('sb-modal-add-' + this.sectionId);
    this.modalClose = this.modalOverlay ? this.modalOverlay.querySelector('.sb__modal-close') : null;
    this._modalCard = null;
    this._modalVariantId = null;
    this._modalPrice = 0;

    this.init();
  }

  StackBuilder.prototype.init = function () {
    this.bindCardClicks();
    this.bindCtaClick();
    this.bindModalEvents();
  };

  StackBuilder.prototype.bindCardClicks = function () {
    var self = this;
    var cards = this.section.querySelectorAll('.sb__card:not(.is-unavailable)');
    cards.forEach(function (card) {
      // Add button - toggle bundle selection
      var addBtn = card.querySelector('.sb__card-btn');
      if (addBtn) {
        addBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.toggleItem(card);
        });
      }

      // Inline variant (flavor) picker - swap the card's active variant without opening the modal
      var variantSelect = card.querySelector('.sb__card-variant');
      if (variantSelect) {
        // Keep taps on the dropdown from bubbling to card/modal handlers
        variantSelect.addEventListener('click', function (e) { e.stopPropagation(); });
        variantSelect.addEventListener('change', function () {
          var opt = variantSelect.options[variantSelect.selectedIndex];
          var vid = parseInt(variantSelect.value, 10);
          var price = parseInt(opt.getAttribute('data-price'), 10);
          var img = opt.getAttribute('data-image');
          card.dataset.sbVariantId = vid;
          if (!isNaN(price)) {
            card.dataset.sbPrice = price;
            var priceEl = card.querySelector('.sb__card-price');
            if (priceEl) priceEl.textContent = self.formatMoney(price);
          }
          // Swap the card image to match the chosen flavor (falls back to the product image)
          if (img) {
            card.dataset.sbImage = img;
            var imgEl = card.querySelector('.sb__card-image img');
            if (imgEl) imgEl.src = img;
          }
          // If this product is already in the bundle, update the stored line + refresh totals/thumbnail
          var item = self.selectedItems[self.findIndex(card.dataset.sbProductId)];
          if (item) {
            item.variant_id = vid;
            if (!isNaN(price)) item.price = price;
            if (img) item.image = img;
            self.updateBar();
          }
        });
      }

      // Card body (image + title) - open quick-view modal
      var imageWrap = card.querySelector('.sb__card-image');
      var titleEl = card.querySelector('.sb__card-title');

      if (imageWrap) {
        imageWrap.style.cursor = 'pointer';
        imageWrap.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.openModal(card);
        });
      }
      if (titleEl) {
        titleEl.style.cursor = 'pointer';
        titleEl.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.openModal(card);
        });
      }
    });
  };

  // ===== Modal =====

  StackBuilder.prototype.bindModalEvents = function () {
    var self = this;
    if (!this.modalOverlay) return;

    // Close button
    if (this.modalClose) {
      this.modalClose.addEventListener('click', function () {
        self.closeModal();
      });
    }

    // Click overlay to close
    this.modalOverlay.addEventListener('click', function (e) {
      if (e.target === self.modalOverlay) {
        self.closeModal();
      }
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.modalOverlay.classList.contains('is-open')) {
        self.closeModal();
      }
    });

    // Add to Bundle button in modal
    if (this.modalAddBtn) {
      this.modalAddBtn.addEventListener('click', function () {
        self.addFromModal();
      });
    }
  };

  StackBuilder.prototype.openModal = function (card) {
    var self = this;
    this._modalCard = card;
    var productUrl = card.dataset.sbUrl;

    // Show loading state
    this.modalTitle.textContent = card.dataset.sbTitle;
    this.modalImg.src = card.dataset.sbImage;
    this.modalImg.alt = card.dataset.sbTitle;
    this.modalPrice.textContent = this.formatMoney(parseInt(card.dataset.sbPrice, 10));
    this.modalDesc.innerHTML = '';
    this.modalVariants.innerHTML = '';
    this._modalVariantId = parseInt(card.dataset.sbVariantId, 10);
    this._modalPrice = parseInt(card.dataset.sbPrice, 10);

    // Update button state
    var isSelected = card.classList.contains('is-selected');
    this.modalAddBtn.textContent = isSelected ? 'Remove from Bundle' : 'Add to Bundle';
    this.modalAddBtn.classList.toggle('is-added', isSelected);

    this.modalOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Fetch full product data
    fetch(productUrl + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        // Description
        if (product.description) {
          var tempDiv = document.createElement('div');
          tempDiv.innerHTML = product.description;
          var text = tempDiv.textContent || tempDiv.innerText || '';
          self.modalDesc.textContent = text;
        }

        // Variants
        if (product.variants && product.variants.length > 1) {
          var available = product.variants.filter(function (v) { return v.available; });
          if (available.length > 0) {
            var label = document.createElement('label');
            var optName = 'Option';
            if (product.options && product.options[0]) {
              optName = typeof product.options[0] === 'string' ? product.options[0] : (product.options[0].name || 'Option');
            }
            label.textContent = optName;

            var select = document.createElement('select');
            available.forEach(function (v) {
              var opt = document.createElement('option');
              opt.value = v.id;
              opt.textContent = v.title + ' - ' + self.formatMoney(v.price);
              if (v.id === self._modalVariantId) opt.selected = true;
              select.appendChild(opt);
            });

            select.addEventListener('change', function () {
              var vid = parseInt(select.value, 10);
              self._modalVariantId = vid;
              var variant = available.find(function (v) { return v.id === vid; });
              if (variant) {
                self._modalPrice = variant.price;
                self.modalPrice.textContent = self.formatMoney(variant.price);
                if (variant.featured_image) {
                  self.modalImg.src = variant.featured_image.src;
                }
              }
            });

            self.modalVariants.appendChild(label);
            self.modalVariants.appendChild(select);
          }
        }
      })
      .catch(function () {
        // Silently fail - we already have basic info from data attributes
      });
  };

  StackBuilder.prototype.closeModal = function () {
    this.modalOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    this._modalCard = null;
  };

  StackBuilder.prototype.addFromModal = function () {
    var card = this._modalCard;
    if (!card) return;

    // Update card's variant data if user changed it in the modal
    card.dataset.sbVariantId = this._modalVariantId;
    card.dataset.sbPrice = this._modalPrice;

    // Mirror the choice onto the card's inline dropdown + price + image so they stay in sync
    var cardSelect = card.querySelector('.sb__card-variant');
    if (cardSelect) {
      cardSelect.value = String(this._modalVariantId);
      var syncedOpt = cardSelect.options[cardSelect.selectedIndex];
      var syncedImg = syncedOpt ? syncedOpt.getAttribute('data-image') : null;
      if (syncedImg) {
        card.dataset.sbImage = syncedImg;
        var cardImgEl = card.querySelector('.sb__card-image img');
        if (cardImgEl) cardImgEl.src = syncedImg;
      }
    }
    var cardPriceEl = card.querySelector('.sb__card-price');
    if (cardPriceEl) cardPriceEl.textContent = this.formatMoney(this._modalPrice);

    this.toggleItem(card);

    // Update button state
    var isSelected = card.classList.contains('is-selected');
    this.modalAddBtn.textContent = isSelected ? 'Remove from Bundle' : 'Add to Bundle';
    this.modalAddBtn.classList.toggle('is-added', isSelected);

    if (isSelected) {
      // Close modal after short delay when adding
      var self = this;
      setTimeout(function () {
        self.closeModal();
      }, 400);
    }
  };

  // ===== Selection & Bar =====

  StackBuilder.prototype.toggleItem = function (card) {
    var productId = card.dataset.sbProductId;
    var idx = this.findIndex(productId);

    if (idx > -1) {
      this.selectedItems.splice(idx, 1);
      card.classList.remove('is-selected');
    } else {
      this.selectedItems.push({
        id: productId,
        variant_id: parseInt(card.dataset.sbVariantId, 10),
        title: card.dataset.sbTitle,
        price: parseInt(card.dataset.sbPrice, 10),
        image: card.dataset.sbImage,
        url: card.dataset.sbUrl
      });
      card.classList.add('is-selected');
    }

    this.updateBar();
  };

  StackBuilder.prototype.findIndex = function (productId) {
    for (var i = 0; i < this.selectedItems.length; i++) {
      if (this.selectedItems[i].id === productId) return i;
    }
    return -1;
  };

  StackBuilder.prototype.removeItem = function (productId) {
    var idx = this.findIndex(productId);
    if (idx === -1) return;

    this.selectedItems.splice(idx, 1);

    var card = this.section.querySelector('.sb__card[data-sb-product-id="' + productId + '"]');
    if (card) card.classList.remove('is-selected');

    this.updateBar();
  };

  StackBuilder.prototype.updateBar = function () {
    var count = this.selectedItems.length;

    // Show/hide sticky bar (also toggle body class so theme.liquid can lift the chat bubble)
    if (count > 0) {
      this.stickyBar.classList.add('is-visible');
      document.body.classList.add('sb-sticky-active');
    } else {
      this.stickyBar.classList.remove('is-visible');
      document.body.classList.remove('sb-sticky-active');
    }

    // Update thumbnails
    this.renderThumbnails();

    // Update count & total (with live stack discount surfaced inline)
    var total = 0;
    for (var i = 0; i < this.selectedItems.length; i++) {
      total += this.selectedItems[i].price;
    }
    this._subtotal = total;
    var barPct = (count >= this.tier2Count) ? this.tier2Pct : (count >= this.tier1Count ? this.tier1Pct : 0);
    var barSavings = Math.round(total * barPct / 100);
    var barLabel = count + ' item' + (count !== 1 ? 's' : '');
    if (this.showDiscountMessaging && barSavings > 0) {
      this.barCount.innerHTML = barLabel + ' \u00B7 <s class="sb__bar-orig">' + this.formatMoney(total) + '</s> ' + this.formatMoney(total - barSavings);
    } else {
      this.barCount.textContent = barLabel + ' \u00B7 ' + this.formatMoney(total);
    }

    // Free-shipping progress (measured on the discounted/payable total to match the cart bar)
    if (this.barShip) {
      var payable = total - barSavings;
      if (!this.showFreeShipping || count === 0) {
        this.barShip.textContent = '';
        this.barShip.classList.remove('is-unlocked');
      } else if (payable >= this.freeShipThreshold) {
        this.barShip.innerHTML = '\u2713 Free shipping included';
        this.barShip.classList.add('is-unlocked');
      } else {
        this.barShip.textContent = 'Spend ' + this.formatMoney(this.freeShipThreshold - payable) + ' more for free shipping';
        this.barShip.classList.remove('is-unlocked');
      }
    }

    // Update tier messaging
    this.updateTierMessage(count);
    this.updateTopTierBar(count);

    // Enable/disable CTA (stamp the dollar savings on the button at the conversion moment)
    this.barCta.disabled = count === 0;
    if (this.showDiscountMessaging && barSavings > 0) {
      this.barCta.innerHTML = this.ctaLabel + ' <span class="sb__cta-save">· Save ' + this.formatMoney(barSavings) + '</span>';
    } else {
      this.barCta.textContent = this.ctaLabel;
    }
    this.barCta.classList.remove('is-added');
  };

  StackBuilder.prototype.renderThumbnails = function () {
    var self = this;
    this.barItems.innerHTML = '';

    this.selectedItems.forEach(function (item) {
      var thumb = document.createElement('div');
      thumb.className = 'sb__bar-thumb';
      thumb.title = 'Remove ' + item.title;
      thumb.innerHTML = '<img src="' + item.image + '" alt="' + item.title.replace(/"/g, '&quot;') + '" width="44" height="44"><button class="sb__bar-thumb-x" aria-label="Remove">&times;</button>';
      thumb.querySelector('.sb__bar-thumb-x').addEventListener('click', function (e) {
        e.stopPropagation();
        self.removeItem(item.id);
      });
      self.barItems.appendChild(thumb);
    });
  };

  StackBuilder.prototype.updateTopTierBar = function (count) {
    if (!this.topTierMsg || !this.topTierBar) return;
    var msg, state;
    var t1 = this.tier1Label;
    var t2 = this.tier2Label;
    if (count === 0) {
      msg = 'Build your stack - pick <strong>2</strong> to save <strong>' + t1 + '</strong> · pick <strong>3+</strong> to save <strong>' + t2 + '</strong>';
      state = 'empty';
    } else if (count < this.tier1Count) {
      var need1 = this.tier1Count - count;
      msg = '<strong>' + count + '</strong> in your stack - add <strong>' + need1 + ' more</strong> to unlock <strong>' + t1 + '</strong>';
      state = 'partial';
    } else if (count < this.tier2Count) {
      var need2 = this.tier2Count - count;
      msg = '✓ <strong>' + t1 + ' unlocked!</strong> Add <strong>' + need2 + ' more</strong> for <strong>' + t2 + '</strong>';
      state = 'unlocked-1';
    } else {
      msg = '✓ <strong>' + t2 + ' unlocked</strong> - discount applies at checkout';
      state = 'unlocked-2';
    }
    this.topTierMsg.innerHTML = msg;
    this.topTierBar.setAttribute('data-state', state);
  };

  StackBuilder.prototype.updateTierMessage = function (count) {
    var msg = '';
    var wasBelowTier2 = this._lastCount < this.tier2Count;

    if (!this.showDiscountMessaging) {
      this.barMessage.textContent = '';
      this._lastCount = count;
      return;
    }

    var a = '<span class="sb__bar-message-pct">';
    var z = '</span>';
    var subtotal = this._subtotal || 0;

    if (count === 0) {
      msg = '';
    } else if (count < this.tier1Count) {
      var needed1 = this.tier1Count - count;
      msg = 'Add ' + needed1 + ' more to unlock ' + a + this.tier1Label + z;
    } else if (count < this.tier2Count) {
      // At tier 1 (e.g. 2 items @ 15%): show current $ saved + quantify the jump to tier 2.
      var curSave = Math.round(subtotal * this.tier1Pct / 100);
      var extraMore = Math.floor(subtotal * (this.tier2Pct - this.tier1Pct) / 10000); // conservative whole-$ floor
      var needed2 = this.tier2Count - count;
      msg = 'You save ' + a + this.formatMoney(curSave) + z +
            ' · Add ' + needed2 + ' more → ' + this.tier2Pct + '% off' +
            (extraMore > 0 ? ' (save $' + extraMore + '+ more)' : '');
    } else {
      // Tier 2 reached: confident, concrete, calm.
      var totSave = Math.round(subtotal * this.tier2Pct / 100);
      msg = 'You save ' + a + this.formatMoney(totSave) + z + ' · ' + this.tier2Pct + '% off applied';
    }

    this.barMessage.innerHTML = msg;

    // Update visual progress bar (clamp 0..1 against tier2 threshold)
    if (this.barProgressFill) {
      var pct = Math.min(count / this.tier2Count, 1) * 100;
      this.barProgressFill.style.width = pct + '%';
    }
    if (this.barProgressStop1) {
      this.barProgressStop1.classList.toggle('is-unlocked', count >= this.tier1Count);
    }
    if (this.barProgressStop2) {
      this.barProgressStop2.classList.toggle('is-unlocked', count >= this.tier2Count);
    }

    // Pulse CTA when hitting tier 2
    if (count >= this.tier2Count && wasBelowTier2) {
      this.barCta.classList.add('is-tier-unlock');
      var cta = this.barCta;
      setTimeout(function () {
        cta.classList.remove('is-tier-unlock');
      }, 600);
    }

    this._lastCount = count;
  };

  StackBuilder.prototype.bindCtaClick = function () {
    var self = this;
    this.barCta.addEventListener('click', function () {
      if (self.selectedItems.length === 0) return;
      self.addToCart();
    });
  };

  StackBuilder.prototype.addToCart = function () {
    var self = this;
    var cta = this.barCta;

    cta.disabled = true;
    cta.textContent = 'Adding...';

    // Generate a unique bundle ID for this stack
    var bundleId = 'stack_' + Date.now();
    var bundleCount = String(this.selectedItems.length);

    var items = this.selectedItems.map(function (item) {
      return {
        id: item.variant_id,
        quantity: 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_name': 'Custom Stack',
          '_bundle_count': bundleCount,
          '_stack_lab': 'true'
        }
      };
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          // Error from Shopify
          cta.textContent = 'Try Again';
          cta.disabled = false;
          return;
        }

        // Success
        cta.classList.add('is-added');
        cta.textContent = 'Added to Cart!';

        // Celebratory toast (auto-dismisses)
        self.showToast(data);

        // Publish cart update event (Enterprise theme pattern)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'stack-builder',
            cartData: data
          });
        }

        // Open cart drawer
        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.renderContents === 'function') {
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cartData) {
              cart.renderContents(cartData);
            });
        }

        // Reset after delay
        setTimeout(function () {
          self.clearSelection();
        }, 2000);
      })
      .catch(function () {
        cta.textContent = 'Try Again';
        cta.disabled = false;
      });
  };

  StackBuilder.prototype.showToast = function (cartAddResponse) {
    if (!this.toast) return;

    var items = (cartAddResponse && cartAddResponse.items) || [];
    var count = 0;
    var savingsCents = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var qty = item.quantity || 1;
      count += qty;
      // /cart/add.js returns final_price (post-discount) and original_price (pre-discount).
      // compare_at_price covers compareAtPrice product savings; line_level_discount covers our EPD discount.
      var pre = (typeof item.original_price === 'number') ? item.original_price : item.price;
      var post = (typeof item.final_price === 'number') ? item.final_price : item.price;
      if (pre > post) {
        savingsCents += (pre - post) * qty;
      }
    }

    var meta = count + ' item' + (count !== 1 ? 's' : '');
    if (savingsCents > 0) {
      meta += ' · You saved ' + this.formatMoney(savingsCents);
    }
    this.toastMeta.textContent = meta;

    var toast = this.toast;
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-visible');

    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 2800);
  };

  StackBuilder.prototype.clearSelection = function () {
    var cards = this.section.querySelectorAll('.sb__card.is-selected');
    cards.forEach(function (card) {
      card.classList.remove('is-selected');
    });
    this.selectedItems = [];
    this.updateBar();
  };

  StackBuilder.prototype.formatMoney = function (cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  // Initialize _lastCount for tier tracking
  StackBuilder.prototype._lastCount = 0;

  // Expose globally
  window.StackBuilder = StackBuilder;
})();
